import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, defer, forkJoin, map, of, tap } from 'rxjs';

import { NachrichtPdfBuilder } from './nachricht-pdf.builder';
import { Nachricht } from '../model/nachricht/nachricht';

declare global {
    var PDFDocument: any;
}

@Injectable({
    providedIn: 'root'
})
export class PdfKitService {
    private static readonly DEFAULT_FILENAME = 'Postfachnachricht.pdf';
    private static readonly REGULAR_FONT_PATH = 'assets/fonts/Roboto-Regular.ttf';
    private static readonly BOLD_FONT_PATH = 'assets/fonts/Roboto-Bold.ttf';

    private readonly fontCache = new Map<string, Uint8Array>();

    constructor(
        private readonly http: HttpClient,
        private readonly nachrichtPdfBuilder: NachrichtPdfBuilder,
        @Inject(DOCUMENT) private readonly document: Document
    ) {}

    public downloadNachrichtPdf(
        nachricht: Nachricht,
        filename: string = PdfKitService.DEFAULT_FILENAME
    ): Observable<void> {
        return defer(() => {
            const PDFDocument = globalThis.PDFDocument;

            if (!PDFDocument) {
                throw new Error('PDFDocument (pdfkit.standalone.js) nicht geladen.');
            }

            const doc = new PDFDocument({
                size: 'A4',
                margin: 50,
                bufferPages: true,
                pdfVersion: '1.5',
                subset: 'PDF/UA',
                tagged: true,
                displayTitle: true,
                lang: 'de-DE',
                info: {
                    Title: filename,
                    Author: 'BA Postfach',
                    Subject: 'Postfachnachricht',
                    Keywords: 'BA, Postfach, Nachricht, PDF'
                }
            });

            return this.registerFonts(doc).pipe(
                tap(() => {
                    this.buildAndDownloadPdf(doc, nachricht, filename);
                }),
                map((): void => undefined)
            );
        });
    }

    private registerFonts(doc: any): Observable<void> {
        return forkJoin({
            regular: this.loadFont(PdfKitService.REGULAR_FONT_PATH),
            bold: this.loadFont(PdfKitService.BOLD_FONT_PATH)
        }).pipe(
            tap(({ regular, bold }) => {
                doc.registerFont('BodyFont', regular);
                doc.registerFont('BodyFont-Bold', bold);
            }),
            map((): void => undefined)
        );
    }

    private loadFont(path: string): Observable<Uint8Array> {
        const cachedFont = this.fontCache.get(path);

        if (cachedFont) {
            return of(cachedFont);
        }

        return this.http.get(path, { responseType: 'arraybuffer' }).pipe(
            map((buffer: ArrayBuffer) => new Uint8Array(buffer)),
            tap((font: Uint8Array) => {
                this.fontCache.set(path, font);
            })
        );
    }

    private buildAndDownloadPdf(
        doc: any,
        nachricht: Nachricht,
        filename: string
    ): void {
        const chunks: Uint8Array[] = [];

        doc.on('data', (chunk: Uint8Array) => {
            chunks.push(new Uint8Array(chunk));
        });

        doc.on('end', () => {
            const blob = new Blob(chunks as any, { type: 'application/pdf' });
            this.triggerDownload(blob, filename);
        });

        this.nachrichtPdfBuilder.build(doc, nachricht, filename);
        doc.end();
    }

    private triggerDownload(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        const link = this.document.createElement('a');

        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        this.document.body.appendChild(link);
        link.click();
        link.remove();

        globalThis.setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 100);
    }
}


